import {
  deleteInvitationMutation,
  sendInvitationReminderMutation,
} from '@/api/invitations/invitationMutations';
import { getInvitationsQuery } from '@/api/invitations/invitationQueries';
import { queryKeys } from '@/api/queryKeys';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { formatDate, isOlderThanThreeDays } from '@/lib/utils/dateUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../DashboardTableAccordion';

const PER_PAGE = 15;

export default function ShowInvitedDoctors() {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uuidToDelete, setUuidToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { data: response, isLoading } = useQuery(getInvitationsQuery('Doctor'));

  const deleteInvitation = useMutation({
    ...deleteInvitationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.invitations.index],
      });
      toast.success('Invitation deleted successfully');
    },
  });

  const sendReminder = useMutation({
    ...sendInvitationReminderMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.invitations.index],
      });
      toast.success('Reminder sent successfully');
    },
  });

  const allInvitations = response?.data || [];
  const total = allInvitations.length;
  const lastPage = Math.ceil(total / PER_PAGE) || 1;
  const start = (page - 1) * PER_PAGE;
  const invitations = allInvitations.slice(start, start + PER_PAGE);
  const paginationMeta =
    total > PER_PAGE
      ? {
          current_page: page,
          last_page: lastPage,
          total,
          per_page: PER_PAGE,
        }
      : undefined;

  useEffect(() => {
    if (total > 0 && page > lastPage) {
      setPage(lastPage);
    }
  }, [total, page, lastPage]);

  const handleDeleteClick = (uuid: string) => {
    setUuidToDelete(uuid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (uuidToDelete) {
      deleteInvitation.mutate(uuidToDelete);
      setDeleteDialogOpen(false);
      setUuidToDelete(null);
    }
  };

  const handleSendReminder = (uuid: string) => {
    sendReminder.mutate(uuid);
  };

  return (
    <>
      <DashboardTableAccordion
        value="Show invited doctors"
        title="Show invited doctors"
        data={invitations.map((inv) => ({ ...inv, id: inv.uuid }))}
        isLoading={isLoading}
        emptyMessage="No invitations found."
        loadingMessage="Loading invitations..."
        paginationMeta={paginationMeta}
        onPageChange={setPage}
        renderHeader={() => (
          <>
            <TableHead className="w-[14%]">Name</TableHead>
            <TableHead className="w-[43%]">Email</TableHead>
            <TableHead className="w-[14%]">Date</TableHead>
            <TableHead className="w-[14%]"></TableHead>
            <TableHead className="w-[14%]"></TableHead>
          </>
        )}
        renderRow={(invitation) => {
          const isStale = isOlderThanThreeDays(invitation.created_at);
          const nameEmailDateClass = isStale
            ? 'text-delete'
            : 'text-table-foreground';
          return (
            <>
              <TableCell className={nameEmailDateClass}>
                {invitation.name} {invitation.surname}
              </TableCell>
              <TableCell className={nameEmailDateClass}>
                {invitation.email}
              </TableCell>
              <TableCell className={nameEmailDateClass}>
                {formatDate(invitation.created_at)}
              </TableCell>
              <TableCell>
                <RelationActionButton
                  onClick={() => handleSendReminder(invitation.uuid)}
                  disabled={sendReminder.isPending}
                >
                  Send reminder
                </RelationActionButton>
              </TableCell>
              <TableCell className="text-right">
                <RelationActionButton
                  onClick={() => handleDeleteClick(invitation.uuid)}
                  className="text-delete"
                  disabled={deleteInvitation.isPending}
                >
                  Delete
                </RelationActionButton>
              </TableCell>
            </>
          );
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete invitation"
        description="Are you sure you want to delete this invitation?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteInvitation.isPending}
      />
    </>
  );
}
